import {
  ClaimProposalDTOSourceEnum,
  ClaimProposalDTOStatusEnum,
  ClaimProposalDTOTypeEnum,
  ClaimWithCheckoutRelationsDTO,
} from '@reposit/api-client';
import { get } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useAppDispatch } from '../..';
import { getClaimResponseDeadlineExtensionsStateByClaimId } from '../../redux/claim-response-deadline-extensions/claim-response-deadline-extensions.selectors';
import { fetchClaimResponseDeadlineExtensionsThunk } from '../../redux/claim-response-deadline-extensions/claim-response-deadline-extensions.thunk';
import { ClaimProposalEntity } from '../../redux/entities/entities.types';
import { AppState } from '../../redux/root.reducer';
import { getFirstAgentProposal, getFirstTenantProposal, getSecondAgentProposal } from '../../redux/selectors/mediation.selectors';
import Button from '../Button';
import RepositCard from '../Reposit/RepositCard';
import { ResponseDeadlineDetailsModal } from '../ResponseDeadlineDetailsModal';
import { Table, Tbody, Td, Thead, Tr } from '../Table';

interface DeadlinesCardProps {
  title: string;
  tenancyOutcomeDeadline?: string;
  claim?: ClaimWithCheckoutRelationsDTO;
  // Given when the user may extend the claim's response deadline.
  onExtendResponseDeadline?: () => void;
}

interface SelectedDeadline {
  label: string;
  proposal: ClaimProposalEntity;
}

const ActionCell = styled(Td)`
  text-align: right;
`;

// A draft, or a tenant counter not yet paid for, has no deadline of its own (see getProposalAwaitingResponseFor in
// tenancy), so the deadline to extend is the latest proposal that is neither.
const isAwaitingResponse = (proposal: ClaimProposalEntity) =>
  proposal.status !== ClaimProposalDTOStatusEnum.DRAFT &&
  !(proposal.type === ClaimProposalDTOTypeEnum.INTERMEDIATE && proposal.status === ClaimProposalDTOStatusEnum.AWAITINGPAYMENT);

const DeadlinesCard: React.FC<DeadlinesCardProps> = ({ claim, title, tenancyOutcomeDeadline, onExtendResponseDeadline }) => {
  const dispatch = useAppDispatch();
  const claimId = get(claim, 'id', '');
  const agentFirstProposal = useSelector((state: AppState) => getFirstAgentProposal(state, claimId));
  const tenantProposal = useSelector((state: AppState) => getFirstTenantProposal(state, claimId));
  const agentFinalProposal = useSelector((state: AppState) => getSecondAgentProposal(state, claimId));
  // No optional chaining: admin-legacy's build cannot compile it.
  const extensionsState = useSelector((state: AppState) => getClaimResponseDeadlineExtensionsStateByClaimId(state, claimId));
  const hasFetchedExtensions = !!extensionsState && extensionsState.hasFetched;
  const extensions = (extensionsState && extensionsState.extensions) || [];
  const extensionsLoadFailed = !!extensionsState && extensionsState.hasFailed;
  const [selectedDeadline, setSelectedDeadline] = useState<SelectedDeadline | undefined>();
  const mediationEnabled = !!(claim && claim.mediationEnabled);
  const substantiationDeadline = claim && claim.substantiationDeadline;

  const fetchExtensions = useCallback(() => {
    dispatch(fetchClaimResponseDeadlineExtensionsThunk({ claimId }));
  }, [claimId, dispatch]);

  useEffect(() => {
    if (claimId && !hasFetchedExtensions) fetchExtensions();
  }, [claimId, hasFetchedExtensions, fetchExtensions]);

  const proposalAwaitingResponse = [agentFinalProposal, tenantProposal, agentFirstProposal].find(
    (proposal) => !!proposal && isAwaitingResponse(proposal),
  );
  // For now only the tenants' deadline can be extended, not the supplier's deadline to answer a tenant counter.
  const extendableProposal =
    proposalAwaitingResponse && proposalAwaitingResponse.source === ClaimProposalDTOSourceEnum.SUPPLIER
      ? proposalAwaitingResponse
      : undefined;

  const extensionsFor = (proposal: ClaimProposalEntity) =>
    extensions.filter((extension) => extension.claimProposalId === proposal.id);

  const renderProposalDeadline = (label: string, proposal: ClaimProposalEntity) => (
    <Tr>
      <Td>{label}</Td>
      <Td>{moment(proposal.responseDeadline).format('DD/MM/YYYY')}</Td>
      <ActionCell>
        <Button noArrow={true} mini="true" onClick={() => setSelectedDeadline({ label, proposal })} type="button">
          View details
        </Button>
      </ActionCell>
    </Tr>
  );

  return (
    <RepositCard flush title={title} tooltip="lorem">
      <Table>
        <Thead>
          <Tr>
            <Td>Type</Td>
            <Td>Deadline</Td>
            <Td />
          </Tr>
        </Thead>
        <Tbody>
          {tenancyOutcomeDeadline && (
            <Tr>
              <Td>Claim publish deadline</Td>
              <Td>{moment(tenancyOutcomeDeadline).format('DD/MM/YYYY')}</Td>
              <Td />
            </Tr>
          )}
          {agentFirstProposal &&
            renderProposalDeadline(
              mediationEnabled ? 'First response deadline for tenant(s)' : 'Claim response deadline',
              agentFirstProposal,
            )}
          {tenantProposal && renderProposalDeadline('Response deadline for supplier', tenantProposal)}
          {agentFinalProposal && renderProposalDeadline('Final response deadline for tenant(s)', agentFinalProposal)}
          {substantiationDeadline && (
            <Tr>
              <Td>Substantiation deadline</Td>
              <Td>{moment(substantiationDeadline).format('DD/MM/YYYY')}</Td>
              <Td />
            </Tr>
          )}
        </Tbody>
      </Table>
      {selectedDeadline ? (
        <ResponseDeadlineDetailsModal
          deadlineLabel={selectedDeadline.label}
          proposal={selectedDeadline.proposal}
          extensions={extensionsFor(selectedDeadline.proposal)}
          onRetryExtensions={extensionsLoadFailed ? fetchExtensions : undefined}
          onDismiss={() => setSelectedDeadline(undefined)}
          onExtend={
            onExtendResponseDeadline && extendableProposal && extendableProposal.id === selectedDeadline.proposal.id
              ? () => {
                  setSelectedDeadline(undefined);
                  onExtendResponseDeadline();
                }
              : undefined
          }
        />
      ) : null}
    </RepositCard>
  );
};

export default DeadlinesCard;
